import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { smoothConfig } from './smoothApi';
import { api } from './api';
import { APP_VERSION } from './version';
import './StatsPage.css';

const BUILD_DATE = process.env.REACT_APP_BUILD_DATE || '';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (e) {
    return dateStr;
  }
};

function StatsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [idInput, setIdInput] = useState(id || '');
  const [teamFilter, setTeamFilter] = useState('');
  // Keep groups to preserve series names and their registrations
  const [groups, setGroups] = useState([]);
  const [showTeamName, setShowTeamName] = useState(true);
  const [hideSeriesNames, setHideSeriesNames] = useState(false);
  const [updateInfo, setUpdateInfo] = useState({ match_count: null, last_update: null });

  useEffect(() => {
    setIdInput(id || '');
    if (id) {
      fetchParticipants(id);
    } else {
      setGroups([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    api.get('/updated')
      .then(response => {
        const d = response?.data || {};
        const matchCount = d.match_count ?? d.matchCount;
        const lastUpdate = d.last_update ?? d.lastUpdate ?? d.date;
        setUpdateInfo({
          match_count: typeof matchCount === 'number' ? matchCount : (matchCount != null ? Number(matchCount) : null),
          last_update: lastUpdate != null && lastUpdate !== '' ? String(lastUpdate) : null,
        });
      })
      .catch(() => {
        setUpdateInfo({ match_count: null, last_update: null });
      });
  }, []);

  // Normalize to array of groups: [{ name, registrations: [] }, ...]
  const normalizeGroups = (data) => {
    if (!data) return [];
    // Direct array of group objects
    if (Array.isArray(data)) {
      return data
        .filter((g) => g && typeof g === 'object')
        .map((g) => ({
          name: g.name ?? g.seriesName ?? g.title ?? '',
          registrations: Array.isArray(g.registrations) ? g.registrations : [],
        }));
    }
    // Common wrapper keys
    for (const key of ['participants', 'groups', 'data', 'result', 'payload']) {
      const wrapped = data?.[key];
      if (Array.isArray(wrapped)) return normalizeGroups(wrapped);
    }
    // Fallback: first array in object
    const firstArray = Object.values(data).find((v) => Array.isArray(v));
    if (Array.isArray(firstArray)) return normalizeGroups(firstArray);
    return [];
  };

  const fetchParticipants = async (eventId) => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const url = `https://smoothcomp.com/en/event/${encodeURIComponent(eventId)}/participants`;
      const response = await axios.post(url, null, smoothConfig);
      const series = normalizeGroups(response.data);
      setGroups(series);
    } catch (err) {
      let message = 'Request failed';
      if (err?.response) {
        message = `HTTP ${err.response.status}`;
      } else if (err?.message) {
        message = err.message;
      }
      setError(
        message +
          ' — If this is a CORS error, a proxy is required to fetch from smoothcomp.com in the browser.'
      );
    } finally {
      setLoading(false);
    }
  };

  const fold = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove diacritics
      .replace(/\s+/g, ' ') // collapse whitespace
      .trim();

  const getPropCaseInsensitive = (obj, candidateKeys) => {
    if (!obj || typeof obj !== 'object') return '';
    // Try known keys directly first
    for (const key of candidateKeys) {
      if (obj[key] != null) return obj[key];
    }
    // Case-insensitive lookup
    const lowerMap = new Map(
      Object.entries(obj).map(([k, v]) => [String(k).toLowerCase(), v])
    );
    for (const key of candidateKeys) {
      const v = lowerMap.get(String(key).toLowerCase());
      if (v != null) return v;
    }
    return '';
  };

  // Removed old flat participants filtering; we now filter inside groups only

  // Filter within series groups (for rendering grouped by series)
  const filteredGroups = useMemo(() => {
    const f = fold(teamFilter);
    const mapFiltered = (g) => ({
      ...g,
      registrations: (g.registrations || []).filter((p) => {
        if (!f) return true;
        const parts = [
          getPropCaseInsensitive(p, ['clubName', 'clubname', 'club_name', 'club']),
          getPropCaseInsensitive(p, ['teamName', 'team']),
          getPropCaseInsensitive(p, ['affiliationName', 'affiliation']),
        ];
        const hay = fold(parts.filter(Boolean).join(' '));
        return hay.includes(f);
      }),
    });
    const mapped = groups.map(mapFiltered);
    // Filter out series with no competitors
    return mapped.filter((g) => Array.isArray(g.registrations) && g.registrations.length > 0);
  }, [groups, teamFilter]);

  const totalDisplayed = useMemo(() => {
    if (!hideSeriesNames) {
      return filteredGroups.reduce(
        (sum, g) => sum + ((g.registrations && g.registrations.length) || 0),
        0
      );
    }
    const seen = new Set();
    filteredGroups.forEach((g) => {
      (g.registrations || []).forEach((p) => {
        const first = String(getPropCaseInsensitive(p, ['firstname', 'firstName', 'first_name']) || '').trim();
        const middle = String(getPropCaseInsensitive(p, ['middlename', 'middleName', 'middle_name']) || '').trim();
        const last = String(getPropCaseInsensitive(p, ['lastname', 'lastName', 'last_name']) || '').trim();
        const name = [first, middle, last].filter(Boolean).join(' ');
        if (name) seen.add(name.toLowerCase());
      });
    });
    return seen.size;
  }, [filteredGroups, hideSeriesNames]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!idInput) return;
    // Update URL for shareability, and fetch
    if (idInput !== id) navigate(`/stats/${encodeURIComponent(idInput)}`);
    fetchParticipants(idInput);
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="stats-controls">
      <div className="controls-row">
        <label className="control">
          <span className="label">Event id</span>
          <input
            className="input"
            type="number"
            value={idInput}
            onChange={(e) => setIdInput(e.target.value)}
            placeholder="e.g. 12345"
          />
        </label>
        <label className="control checkbox">
          <input
            type="checkbox"
            checked={showTeamName}
            onChange={(e) => setShowTeamName(e.target.checked)}
          />
          <span>show team name</span>
        </label>
        <label className="control checkbox">
          <input
            type="checkbox"
            checked={hideSeriesNames}
            onChange={(e) => setHideSeriesNames(e.target.checked)}
          />
          <span>hide series names</span>
        </label>
        <label className="control">
          <span className="label">Team name filter</span>
          <input
            className="input"
            type="text"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            placeholder="clubName contains…"
          />
        </label>
        <button type="submit" className="btn">Submit</button>
      </div>
    </form>
  );

  return (
    <div className="stats-wrap">
      <button type="button" className="back-btn" onClick={() => navigate(-1)} title="Go back">&#8592; Back</button>
      <h2 className="page-title">Stats {id ? `for event ${id}` : ''}</h2>
      {renderForm()}
      {loading && <div>Loading…</div>}
      {error && (
        <div style={{ color: 'salmon', whiteSpace: 'pre-wrap' }}>{error}</div>
      )}
      {!loading && !error && groups && groups.length > 0 && (
        <>
          <div className="summary">
            Series: {filteredGroups.length} — Competitors: {totalDisplayed}
            {teamFilter ? ` — filtered by club contains "${teamFilter}"` : ''}
          </div>
          {hideSeriesNames ? (
            // Single combined list with unique competitors
            <div className="series-card">
              <div className="competitor-list">
                {(() => {
                  const seen = new Set();
                  const unique = [];
                  filteredGroups.forEach((grp) => {
                    (grp.registrations || []).forEach((p) => {
                      const first = String(getPropCaseInsensitive(p, ['firstname', 'firstName', 'first_name']) || '').trim();
                      const middle = String(getPropCaseInsensitive(p, ['middlename', 'middleName', 'middle_name']) || '').trim();
                      const last = String(getPropCaseInsensitive(p, ['lastname', 'lastName', 'last_name']) || '').trim();
                      const name = [first, middle, last].filter(Boolean).join(' ');
                      const key = name.toLowerCase();
                      if (!seen.has(key) && name) {
                        seen.add(key);
                        const club = String(getPropCaseInsensitive(p, ['clubName', 'clubname', 'club_name', 'club']) || '').trim();
                        unique.push({ name, club });
                      }
                    });
                  });
                  return unique.map((u, i) => (
                    <div key={i} className="competitor-item">
                      <Link className="competitor-name" to={`/${encodeURIComponent(u.name)}`} title={`Open ${u.name}`}>
                        {u.name}
                      </Link>
                      {showTeamName && u.club ? (
                        <span className="competitor-club">{u.club}</span>
                      ) : null}
                    </div>
                  ));
                })()}
              </div>
            </div>
          ) : (
            filteredGroups.map((grp, idx) => (
              <div key={idx} className="series-card">
                <h3 className="series-title">{grp.name || 'Unnamed series'}</h3>
                <div className="competitor-list">
                  {grp.registrations.map((p, i) => {
                    const first = String(getPropCaseInsensitive(p, ['firstname', 'firstName', 'first_name']) || '').trim();
                    const middle = String(getPropCaseInsensitive(p, ['middlename', 'middleName', 'middle_name']) || '').trim();
                    const last = String(getPropCaseInsensitive(p, ['lastname', 'lastName', 'last_name']) || '').trim();
                    const name = [first, middle, last].filter(Boolean).join(' ');
                    const club = String(getPropCaseInsensitive(p, ['clubName', 'clubname', 'club_name', 'club']) || '').trim();
                    return (
                      <div key={i} className="competitor-item">
                        <Link
                          className="competitor-name"
                          to={`/${encodeURIComponent(name)}`}
                          title={`Open ${name}`}
                        >
                          {name}
                        </Link>
                        {showTeamName && club ? (
                          <span className="competitor-club">{club}</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </>
      )}

      <footer className="footer">
        <span className="version-badge">
          Matches: {updateInfo.match_count != null ? updateInfo.match_count.toLocaleString() : '—'}, Last update {updateInfo.last_update ? formatDate(updateInfo.last_update) : '—'}, Build {BUILD_DATE || '—'}, {APP_VERSION}
        </span>
      </footer>

    </div>
  );
}

export default StatsPage;
